import { Controller } from "@hotwired/stimulus"
import { createLogger, nodeToString } from "@/javascript/utils/logging"

// Stimulus controller that manages form elements which depend on other elements' values
// This controller coordinates multiple dependent-form-element controllers within a form
// Tracks changes to "parent" fields and updates dependent elements via Turbo Streams
export default class extends Controller {
  static targets = [ "" ]

  connect() {
    this.logger = createLogger('DependentFormElementsController')

    this.dependentElementSelector = '[data-controller="dependent-form-element"]'
    this.element.tagName == 'form' ? this.form = this.element : this.form = this.element.closest('form')
    // The CSRF token is needed to be able to make POST requests
    this.token = document.querySelector('meta[name="csrf-token"]').content;
    this.element.addEventListener('change', this.formChanged.bind(this))
    // Track if form has been interacted with
    this.formTouched = false
  }

  // Helper to get the dependent-form-element controller associated with an element
  // This needs to wait for the dependent-form-element controllers to connect
  // as we may have just replaced the dependent elements, and the controllers
  // for the new elements may not be present yet
  //
  // TODO: The only reason we need to access the controllers on the dependent elements
  // is to access these functions:
  //   - reRenderUrl()
  //   - parentFieldElements()
  // Perhaps we should just define these functions on this controller?
  async dependentElementController(element) {
    const retries = 10;
    const delay = 30;
  
    this.logger.debug(`dependentElementController - Waiting for controller to connect for element ${this.nodeToString(element)}`);
  
    for (let attempt = 0; attempt <= retries; attempt++) {
      const ctrl = this.application.getControllerForElementAndIdentifier(element, 'dependent-form-element');
  
      if (ctrl) {
        this.logger.debug(`dependentElementController - Controller found for element ${this.nodeToString(element)}`);
        return ctrl;
      }
  
      if (attempt < retries) {
        this.logger.trace(`dependentElementController - No controller found, waiting ${delay}ms (attempt ${attempt + 1}/${retries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  
    this.logger.debug(`dependentElementController - No controller found after ${retries} attempts`);
    return null;
  }
  

  // Builds collections of parent fields and their dependencies
  async setParentFields() {
    this.logger.debug('setParentFields - started')
    // Find all dependent form element controllers in this form
    this.dependentElements = this.element.querySelectorAll(this.dependentElementSelector)
    this.dependentElementDetails = []
    this.parentFields = []
    
    for (const dependentElement of this.dependentElements) {
      let controller = await this.dependentElementController(dependentElement)

      this.parentFields.push(...controller.parentFieldElements())
      // Store all details needed to update this element when its parents change
      const config = {
        dependentElement: dependentElement,
        url: controller.reRenderUrl(),
        parentFields: controller.parentFieldElements(),
        innerField: this.getInnerField(dependentElement)
      };
              
      this.dependentElementDetails.push(config)
    }
    this.parentFields = [...new Set(this.parentFields.flat())].filter((el) => el != null)
    this.logger.trace(`setParentFields - parentFields: ${this.parentFields}`)
  }

  // Called when a new dependent element is connected to the DOM
  // See dependent_form_element_controller.js for more details
  dependentElementConnected(dependentElement) {
    this.logger.debug(`dependentElementConnected - connected: ${this.nodeToString(dependentElement)}`)

    const innerField = this.getInnerField(dependentElement)
    
    if (innerField) {
      this.logger.debug(`dependentElementConnected - Inner field found: ${this.nodeToString(innerField)}`)

      if (this.formTouched) {
        this.logger.debug(`dependentElementConnected - Form was touched, updating dependents of field: ${this.nodeToString(innerField)}`)

        this.updateDependentsOfField(innerField)
      } else {
        this.logger.debug(`dependentElementConnected - Form was not touched, skipping update of dependents of field: ${this.nodeToString(innerField)}`)
      }
    } else {
      this.logger.debug(`dependentElementConnected - No inner field found`)
    }
  }

  getInnerField(dependentElement) {
    let fieldsSelector = ['input', 'select', 'button']

    if (fieldsSelector.includes(dependentElement.tagName.toLowerCase())) {
      return dependentElement
    }
    return dependentElement.querySelector(fieldsSelector.join(','))
  }


  // Checks if a field is in the list of parent fields
  isParentField(field) {
    return this.parentFields.find((element) => element.id == field.id) !== undefined
  }
  
  // Check if a field directly depends on a parent field
  isDirectDependentOf(elementDetails, parentField) {
    return elementDetails.parentFields.find(element => element.id == parentField.id) !== undefined;
  }
  
  // Finds elements that directly depend on the specified parent field
  getDirectDependentsOf(parentField) {
    return this.dependentElementDetails
      .filter(details => this.isDirectDependentOf(details, parentField))
      .map(details => details.dependentElement);
  }

  // Recursively finds all elements that depend on the specified field
  // Handles chains of dependencies (A depends on B which depends on C)
  // Returns the container elements (dependentElement) of all dependent elements
  getAllDependentsRecursively(field) {
    let dependencies = []
    this.dependentElementDetails.forEach(elementDetails => {
      if(this.isDirectDependentOf(elementDetails, field)){
        dependencies.push(elementDetails.dependentElement)
        if (elementDetails.innerField) {
          dependencies.push(this.getAllDependentsRecursively(elementDetails.innerField))
        }
      }
    })
    return [...new Set(dependencies.flat())].filter((el) => el != null)
  }

  // Processes a change to a parent field
  // Sets dependent elements to "busy" state and triggers updates
  async updateDependentsOfField(field) {
    this.logger.debug(`updateDependentsOfField - started for field: ${this.nodeToString(field)}`)

    // Refresh field mappings in case DOM has changed
    await this.setParentFields()
    // Skip if the changed field isn't on our parent fields list
    if(!this.isParentField(field)){
      this.logger.debug(`updateDependentsOfField - Field is not a parent field ${this.nodeToString(field)}`)

      return
    }
    
    this.logger.debug(`updateDependentsOfField - Processing parent field ${this.nodeToString(field)}`)
    
    let allDependencies = this.getAllDependentsRecursively(field)
    if(allDependencies && allDependencies.length > 0){
      this.markElementsAsBusy(allDependencies)
    }
    
    // Update all elements that directly depend on the changed field
    this.dependentElementDetails.forEach(elementDetails => {
      this.logger.trace(`updateDependentsOfField - Checking if ${nodeToString(elementDetails.dependentElement)} is a direct dependent of ${nodeToString(field)}`)

      if(this.isDirectDependentOf(elementDetails, field)) {
        this.logger.debug(`updateDependentsOfField -  direct dependent, fetching updated content`)

        this.fetchUpdatedElementContent(elementDetails.url)
      } else {
        this.logger.trace(`updateDependentsOfField - not a direct dependent, skipping`)
      }
    })
  }

  formChanged(event) {
    this.formTouched = true
    this.updateDependentsOfField(event.target)
  }

  // Collects and prepares form data for submission
  collectFormData() {
    let formData = new FormData(this.form)
    let params = new URLSearchParams()
    
    // Convert form data to URL parameters, excluding _method field
    formData.forEach((value, key) => {
      if(key=='_method'){
        return
      }
      params.append(key, value)
    })
    
    return params
  }

  // Marks the given container elements as busy to indicate they're being updated
  markElementsAsBusy(containerElements) {
    containerElements.forEach(container => {
      container.closest('[data-turbo-stream-target]').ariaBusy = true
    })
    // Also mark the form as busy
    this.form.setAttribute('aria-busy', 'true')
  }

  // Removes busy state from form and all elements
  clearFormBusyState() {
    this.form.removeAttribute('aria-busy')
    // Clear busy state from all turbo stream targets
    this.form.querySelectorAll('[data-turbo-stream-target]').forEach(target => {
      target.ariaBusy = false
    })
  }

  // Fetches updated element content from the server using current form values
  fetchUpdatedElementContent(url) {
    this.logger.debug(`fetchUpdatedElementContent - url: ${url}`)
    
    let params = this.collectFormData()

    return fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.token
      },
      credentials: 'same-origin',
      body: params
    }).then (response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html)
      this.clearFormBusyState()
    })
    .catch(error => {
      this.clearFormBusyState()
      throw error
    })
  }

  nodeToString(node) {
    return node.tagName + '#' + node.id + '.' + node.className
  }
}
